/**
 * @fileoverview SoundCheckRenderer component for the Storyteller web application.
 * Renders an audio player with a sound check prompt modal.
 * Supports i18n translations and error handling.
 *
 * @example
 * // Basic usage
 * <SoundCheckRenderer
 *   schema={{
 *     type: 'string',
 *     options: {
 *       audio: { file: 'sound-check.mp3', duration: 10 },
 *     }
 *   }}
 *   path="soundCheck"
 *   handleChange={(path, value) => console.log(path, value)}
 * />
 */

import { ControlProps, rankWith, uiTypeIs } from '@jsonforms/core'
import { withJsonFormsControlProps } from '@jsonforms/react'
import { VanillaRendererProps } from '@jsonforms/vanilla-renderers'
import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MiddlewareContext } from '../../../../MiddlewareContext'
import { ModalContext } from '../../../../ModalContext'
import { AudioPlayer } from '../../../AudioPlayer/AudioPlayer'
import { PromptModalContent } from '../../PromptModalContent'

import config from '../../../../config'

/**
 * @function SoundCheckRenderer
 * @description Component for rendering a sound check prompt with an audio player.
 * @param {ControlProps & VanillaRendererProps} props - The props for the SoundCheckRenderer component.
 * @returns {JSX.Element} The SoundCheckRenderer component.
 */
export function SoundCheckRenderer({ uischema, path }: ControlProps & VanillaRendererProps) {
    const audio = uischema.options?.audio as {
        file: string
        duration: number
    }

    const { t } = useTranslation()
    const [isDisabled, setIsDisabled] = useState<boolean>(false)
    const { submit: onSubmit, question } = useContext(MiddlewareContext)

    const modal = useContext(ModalContext)

    async function onConfirm() {
        setIsDisabled(true)
        await onSubmit(question.id, {
            [path]: true,
        })
    }

    function onClose() {
        setIsDisabled(false)
        return Promise.resolve()
    }

    async function onNoSound() {
        setIsDisabled(true)
        await onSubmit(question.id, {
            [path]: false,
        })
    }

    return (
        <AudioPlayer
            audio={{
                file: config.s3.getStaticURL(t(audio.file)),
                duration: parseInt(t(`${audio.duration}`)),
            }}
            titles={{ start: t('soundCheck.audioStart'), stop: t('soundCheck.audioStop') }}
            isDisabled={isDisabled}
            onEnded={() => {
                modal.open({
                    render: ({ onConfirm: defaultConfirm, onCancel: defaultCancel }) => (
                        <PromptModalContent
                            caption={t('modals.soundCheck.caption')}
                            onConfirm={async () => {
                                await onConfirm()
                                defaultConfirm()
                            }}
                            onCancel={async () => {
                                await onNoSound()
                                defaultCancel()
                            }}
                        />
                    ),
                    onClose,
                })
            }}
        />
    )
}

export const SoundCheckTester = rankWith(3, uiTypeIs('SoundCheck'))

export default withJsonFormsControlProps(SoundCheckRenderer)
